// Localização: ./src/context/AIContext.jsx
// Context API para gerenciar estado do chat do assistente de IA

import React, { createContext, useContext, useState, useCallback } from 'react';

const AIContext = createContext();

export const useAI = () => {
    const context = useContext(AIContext);
    if (!context) {
        throw new Error('useAI deve ser usado dentro de AIProvider');
    }
    return context;
};

export const AIProvider = ({ children }) => {
    // Estado das mensagens
    const [messages, setMessages] = useState(() => {
        console.log('[AIContext] AIProvider montado - estado inicial vazio');
        return [];
    });
    
    // Estado da sessão
    const [sessionId, setSessionId] = useState(null);
    
    // Estado de loading
    const [isLoading, setIsLoading] = useState(false);
    
    // Estado de erro
    const [error, setError] = useState(null);
    
    // Estado de ação pendente
    const [pendingAction, setPendingAction] = useState(null);
    
    // Adiciona uma mensagem ao chat
    const addMessage = useCallback((message) => {
        console.log('[AIContext] addMessage chamado - adicionando mensagem:', message.role);
        setMessages(prev => [...prev, message]);
    }, []);
    
    // Limpa todas as mensagens e reinicia a sessão
    const clearChat = useCallback(() => {
        console.log('[AIContext] clearChat chamado - limpando mensagens');
        setMessages([]);
        setSessionId(null);
        setPendingAction(null);
        setError(null);
    }, []);
    
    // Atualiza o sessionId
    const updateSessionId = useCallback((newSessionId) => {
        setSessionId(newSessionId);
    }, []);
    
    // Define o estado de loading
    const setLoading = useCallback((loading) => {
        setIsLoading(loading);
    }, []);
    
    // Define o erro
    const setErrorState = useCallback((errorMessage) => {
        setError(errorMessage);
    }, []);
    
    // Limpa o erro
    const clearError = useCallback(() => {
        setError(null);
    }, []);
    
    // Define ação pendente
    const setPendingActionState = useCallback((action) => {
        setPendingAction(action);
    }, []);
    
    // Limpa ação pendente
    const clearPendingAction = useCallback(() => {
        setPendingAction(null);
    }, []);

    // Função removida - mensagens de sucesso/erro agora vêm diretamente do backend

    // Carrega uma conversa selecionada (agora carrega toda a sessão)
    const loadConversation = useCallback(async (conversation) => {
        if (!conversation || !conversation.session_id) return;
        
        try {
            // Importar dinamicamente para evitar dependência circular
            const { getConversationsBySession } = await import('../api/aiApi');
            
            const response = await getConversationsBySession(conversation.session_id);
            
            if (response.data.success && response.data.conversations) {
                const loadedMessages = [];
                
                // Processar todas as mensagens da sessão em ordem cronológica
                response.data.conversations.forEach((conv) => {
                    // Adiciona mensagem do usuário
                    if (conv.mensagem_usuario) {
                        loadedMessages.push({
                            id: `user-${conv.id}`,
                            role: 'user',
                            content: conv.mensagem_usuario,
                            timestamp: new Date(conv.created_at)
                        });
                    }
                    
                    // Adiciona resposta da IA
                    if (conv.mensagem_ia) {
                        loadedMessages.push({
                            id: `assistant-${conv.id}`,
                            role: 'assistant',
                            content: conv.mensagem_ia,
                            timestamp: new Date(conv.created_at),
                            intent: conv.intent,
                            entities: conv.entities ? (typeof conv.entities === 'string' ? JSON.parse(conv.entities) : conv.entities) : {},
                            actionResult: conv.action_result ? (typeof conv.action_result === 'string' ? JSON.parse(conv.action_result) : conv.action_result) : null
                        });
                        
                        // Não adicionar mensagens de sistema ao carregar conversas
                        // A mensagem da IA já contém a resposta apropriada (sucesso ou erro)
                    }
                });
                
                setMessages(loadedMessages);
                setSessionId(conversation.session_id);
            }
        } catch (error) {
            console.error('[AIContext] Erro ao carregar conversa:', error);
            // Fallback: carregar apenas a mensagem selecionada
            const loadedMessages = [];
            if (conversation.mensagem_usuario) {
                loadedMessages.push({
                    id: `user-${conversation.id}`,
                    role: 'user',
                    content: conversation.mensagem_usuario,
                    timestamp: new Date(conversation.created_at)
                });
            }
            if (conversation.mensagem_ia) {
                loadedMessages.push({
                    id: `assistant-${conversation.id}`,
                    role: 'assistant',
                    content: conversation.mensagem_ia,
                    timestamp: new Date(conversation.created_at),
                    intent: conversation.intent
                });
            }
            setMessages(loadedMessages);
            if (conversation.session_id) {
                setSessionId(conversation.session_id);
            }
        }
    }, []);
    
    const value = {
        // Estado
        messages,
        sessionId,
        isLoading,
        error,
        pendingAction,
        
        // Ações
        addMessage,
        clearChat,
        updateSessionId,
        setLoading,
        setError: setErrorState,
        clearError,
        setPendingAction: setPendingActionState,
        clearPendingAction,
        loadConversation
    };
    
    return (
        <AIContext.Provider value={value}>
            {children}
        </AIContext.Provider>
    );
};

